// src/pages/searchMatch.ts
import type { Conversation } from '../types';

/**
 * Приводим ввод и данные к сравнимому виду.
 *
 * Поиск по чатам идёт на клиенте по уже загруженному списку, поэтому
 * сравнение должно быть одинаковым для латиницы и кириллицы: регистр и
 * лишние пробелы по краям значения не имеют.
 */
export const normalizeSearchQuery = (value: string): string => value.trim().toLowerCase();

/** Совпадает ли значение с запросом по подстроке без учёта регистра. */
export const matchesSearchQuery = (value: string | null | undefined, query: string): boolean => {
    const needle = normalizeSearchQuery(query);
    if (needle === '') return false;

    return normalizeSearchQuery(value ?? '').includes(needle);
};

/**
 * Подпись чата для списка результатов.
 *
 * У группы название лежит в `title`, у личного чата — в имени контакта;
 * возможны оба поля или ни одного (пустой контакт в только что созданном чате).
 */
export const conversationSearchTitle = (conversation: Conversation): string => {
    const candidates = [conversation.title, conversation.contact?.mainName];
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim() !== '') return candidate.trim();
    }

    return 'Без названия';
};

/**
 * Чаты, в названии или телефоне которых встречается запрос.
 *
 * Поиск по сообщениям — отдельный этап: здесь смотрим только название чата
 * и номер собеседника, иначе результат обещал бы то, чего ещё нет.
 */
export const matchConversations = (
    conversations: readonly Conversation[],
    query: string,
): Conversation[] => {
    if (normalizeSearchQuery(query) === '') return [];

    return conversations.filter(
        (conversation) =>
            matchesSearchQuery(conversationSearchTitle(conversation), query) ||
            matchesSearchQuery(conversation.contact?.phone, query),
    );
};
